import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Modal } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";

import {
  useFinalizeInspectionMutation,
  type InspectionFinalizeResponse,
} from "../../../api/inspection";
import { formatInspectionDate } from "../../../utils/inspectionDate";

export interface InspectionFinalizeConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  structureId: string;
  inspectionId: string;
  inspectionCode?: number;
  inspectionDate?: string;
  signedCount?: number;
  totalParticipants?: number;
  anomalyCount?: number | null;
  conservationStateScore?: number | null;
  onFinalized?: (finalized: InspectionFinalizeResponse) => void;
}

/**
 * Confirmação de finalização (PRD §6 / quality-test §9). Visual alinhado ao
 * protótipo (ícone verde, resumo, warn-box irreversível).
 */
const InspectionFinalizeConfirmModal = ({
  isOpen,
  onClose,
  structureId,
  inspectionId,
  inspectionCode,
  inspectionDate,
  signedCount,
  totalParticipants,
  anomalyCount,
  conservationStateScore,
  onFinalized,
}: InspectionFinalizeConfirmModalProps) => {
  const { translate, language } = useLocalization();
  const { mutate: finalizeInspection, isPending } =
    useFinalizeInspectionMutation(structureId, inspectionId);

  const handleConfirm = () =>
    finalizeInspection(undefined, {
      onSuccess: (finalized) => onFinalized?.(finalized),
    });

  const hasSummary =
    inspectionCode !== undefined ||
    inspectionDate !== undefined ||
    signedCount !== undefined ||
    anomalyCount !== undefined ||
    conservationStateScore !== undefined;

  return (
    <Modal
      open={isOpen}
      onClose={isPending ? () => undefined : onClose}
      title={
        <span className="insp-finalize-head-inner">
          <span className="insp-finalize-head-icon" aria-hidden>
            <CheckCircle2 size={22} />
          </span>
          <span className="insp-finalize-head-copy">
            <span>{translate("inspection.finalize.title")}</span>
            <p className="insp-finalize-sub">
              {translate("inspection.finalize.subtitle")}
            </p>
          </span>
        </span>
      }
      width="620px"
      content={
        <div className="insp-nested-shell insp-nested-shell--wide">
          <div className="insp-nested-body flex flex-col gap-4">
          {hasSummary ? (
            <div className="insp-finalize-summary">
              {inspectionCode !== undefined ? (
                <div className="insp-finalize-row">
                  <span>{translate("inspection.finalize.code")}</span>
                  <strong>#{inspectionCode}</strong>
                </div>
              ) : null}
              {inspectionDate !== undefined ? (
                <div className="insp-finalize-row">
                  <span>{translate("inspection.finalize.inspectionDate")}</span>
                  <strong>{formatInspectionDate(inspectionDate, language)}</strong>
                </div>
              ) : null}
              {signedCount !== undefined && totalParticipants !== undefined ? (
                <div className="insp-finalize-row">
                  <span>{translate("inspection.finalize.signatures")}</span>
                  <strong>
                    {translate("inspection.finalize.signaturesOf", {
                      signed: signedCount,
                      total: totalParticipants,
                    })}
                  </strong>
                </div>
              ) : null}
              {anomalyCount !== undefined ? (
                <div className="insp-finalize-row">
                  <span>{translate("inspection.finalize.anomalyCount")}</span>
                  <strong>
                    {anomalyCount ?? translate("inspection.table.fallback")}
                  </strong>
                </div>
              ) : null}
              {conservationStateScore !== undefined ? (
                <div className="insp-finalize-row">
                  <span>{translate("inspection.finalize.ecSum")}</span>
                  <strong>
                    {conservationStateScore ??
                      translate("inspection.table.fallback")}
                  </strong>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="insp-finalize-warn">
            <AlertTriangle aria-hidden size={20} className="shrink-0" />
            <p className="m-0">
              {translate("inspection.finalize.warningBefore")}
              <strong>
                {translate("inspection.finalize.warningIrreversible")}
              </strong>
              {translate("inspection.finalize.warningAfter")}
            </p>
          </div>

          </div>

          <div className="insp-modal-foot">
            <button
              type="button"
              className="insp-btn insp-btn-outline"
              disabled={isPending}
              onClick={isPending ? undefined : onClose}
            >
              {translate("inspection.finalize.cancel")}
            </button>
            <button
              type="button"
              disabled={isPending}
              aria-busy={isPending || undefined}
              className="insp-btn insp-btn-primary disabled:opacity-60"
              onClick={handleConfirm}
            >
              {isPending ? (
                <Loader2 aria-hidden size={18} className="animate-spin" />
              ) : (
                <CheckCircle2 aria-hidden size={18} />
              )}
              {translate(
                isPending
                  ? "inspection.finalize.confirming"
                  : "inspection.finalize.confirm",
              )}
            </button>
          </div>
        </div>
      }
    />
  );
};

export default InspectionFinalizeConfirmModal;
